import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Bot,
  Users,
  Github,
  CheckCircle,
  Code,
  Briefcase,
  Target,
  Mail,
  Heart,
  TrendingUp,
} from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import FadeIn from '../components/ui/FadeIn';
import BackToTop from '../components/ui/BackToTop';

const features = [
  {
    icon: BookOpen,
    title: 'Structured Learning Paths',
    description: 'Follow curated learning tracks designed for career changers, from basics to advanced concepts.',
  },
  {
    icon: Bot,
    title: 'AI-Powered Mentorship',
    description: 'Get personalized code reviews, learning recommendations, and 24/7 support from Claude AI.',
  },
  {
    icon: Users,
    title: 'Expert Mentorship',
    description: 'Connect with industry professionals for guidance, career advice, and networking opportunities.',
  },
  {
    icon: Github,
    title: 'Project Portfolio',
    description: 'Build and showcase real-world projects with integrated GitHub workflow and collaboration.',
  },
];

const benefits = [
  'Personalized learning paths based on your goals',
  'Real-time AI feedback on your code',
  'Industry mentor matching system',
  'Portfolio building with GitHub integration',
  'Progress tracking and analytics',
  'Community support and networking',
];

const impactStats = [
  {
    id: 'recidivism',
    value: '85%',
    label: 'Reduction in recidivism',
    detail: 'when individuals maintain stable employment',
  },
  {
    id: 'tech-jobs',
    value: '3.5M',
    label: 'Unfilled U.S. tech jobs',
    detail: 'a workforce gap our graduates are positioned to fill',
  },
  {
    id: 'salary',
    value: '$55–75K',
    label: 'Average starting salary',
    detail: 'for graduates entering technology roles',
  },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content">
        {/* Hero Section */}
        <section
          aria-labelledby="hero-heading"
          className="relative bg-secondary-950 py-24 sm:py-32 overflow-hidden"
        >
          {/* Watermark banner logo — barely visible brand imprint */}
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <img
              src="/brand/2ndStoryBanner.png"
              alt=""
              className="w-[600px] sm:w-[800px] lg:w-[1000px] max-w-none opacity-[0.04] select-none"
              draggable="false"
            />
          </div>

          {/* Animated gradient overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-15 animate-gradient-shift"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 50%, rgba(53,94,59,0.4) 0%, transparent 50%), radial-gradient(circle at 75% 20%, rgba(53,94,59,0.2) 0%, transparent 45%)',
              backgroundSize: '200% 200%',
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-8">
              <img
                src="/brand/logo-200.png"
                alt="Second Story"
                className="h-24 w-24 sm:h-28 sm:w-28 mx-auto rounded-full bg-white/95 p-1 shadow-lg"
                loading="eager"
              />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-900/30 backdrop-blur-sm border border-primary-700/20 px-4 py-1.5 text-sm font-medium text-primary-300 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-success-500" />
              </span>
              RI Nonprofit · Now Enrolling
            </div>
            <h1
              id="hero-heading"
              className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight"
            >
              Your <span className="text-primary-400">Second</span>{' '}
              <span className="text-primary-300">Story</span>{' '}Starts Here
            </h1>
            <p className="text-lg sm:text-xl text-secondary-300 max-w-2xl mx-auto leading-relaxed max-w-none">
              Empowering justice-impacted individuals to build meaningful careers in technology
              through education, mentorship, and community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link
                to="/register"
                className="btn btn-lg bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center shadow-medium no-underline hover:no-underline"
              >
                Start Your Journey
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
              <Link
                to="/about"
                className="btn btn-lg border-2 border-white/70 text-white hover:bg-white/10 inline-flex items-center no-underline hover:no-underline"
              >
                Learn About Our Mission
              </Link>
            </div>
          </div>
        </section>

        {/* Impact Stats Banner */}
        <section aria-label="Key impact statistics" className="bg-white border-b border-secondary-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-secondary-100">
              {impactStats.map((stat) => (
                <div key={stat.id} className="py-8 px-6 text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-sm font-semibold text-secondary-900 mb-1">{stat.label}</div>
                  <div className="text-xs text-secondary-500 leading-snug max-w-none">{stat.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section aria-labelledby="features-heading" className="py-20 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn className="text-center mb-16">
              <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">
                Our Platform
              </p>
              <h2 id="features-heading" className="text-3xl font-bold text-secondary-900 mb-4">
                Everything You Need to Succeed
              </h2>
              <p className="text-lg text-secondary-600 max-w-2xl mx-auto max-w-none">
                A comprehensive platform providing the tools and support for a successful
                career transition into technology.
              </p>
            </FadeIn>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <FadeIn key={index} delay={index * 100}>
                <div className="card text-center">
                  <div className="card-content">
                    <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-secondary-600 max-w-none">{feature.description}</p>
                  </div>
                </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section aria-labelledby="benefits-heading" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <FadeIn>
                <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">
                  Why Second Story
                </p>
                <h2 id="benefits-heading" className="text-3xl font-bold text-secondary-900 mb-6">
                  Built for Real Career Transitions
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-success-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-secondary-700 max-w-none">{benefit}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link
                    to="/register"
                    className="btn btn-primary inline-flex items-center no-underline hover:no-underline"
                  >
                    Join Our Community
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </FadeIn>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-soft">
                      <Code className="h-8 w-8 text-primary-600 mb-2" aria-hidden="true" />
                      <div className="text-sm font-medium text-secondary-900">Learn to Code</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-soft">
                      <Bot className="h-8 w-8 text-accent-600 mb-2" aria-hidden="true" />
                      <div className="text-sm font-medium text-secondary-900">AI Mentorship</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-soft">
                      <Users className="h-8 w-8 text-success-600 mb-2" aria-hidden="true" />
                      <div className="text-sm font-medium text-secondary-900">Expert Guidance</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-soft">
                      <Briefcase className="h-8 w-8 text-warning-600 mb-2" aria-hidden="true" />
                      <div className="text-sm font-medium text-secondary-900">Land Your Job</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Projects Preview */}
        <section aria-labelledby="projects-heading" className="py-20 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="text-center mb-12">
                <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">
                  What We're Building
                </p>
                <h2 id="projects-heading" className="text-3xl font-bold text-secondary-900 mb-4">
                  Justice-Tech Projects
                </h2>
                <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
                  Real tools addressing real gaps in the justice-to-tech pipeline —
                  built by and for the community we serve.
                </p>
              </div>
            </FadeIn>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[
                {
                  title: 'Learning Platform',
                  status: 'Live',
                  statusColor: 'bg-success-50 text-success-700 border-success-200',
                  summary: 'AI-powered education with Claude mentoring, learning paths, and GitHub portfolio integration.',
                  slug: 'learning-platform',
                },
                {
                  title: 'AI Orchestrator',
                  status: 'In Development',
                  statusColor: 'bg-blue-50 text-blue-700 border-blue-200',
                  summary: 'Intelligent workflow engine coordinating AI models and human mentors for adaptive learning.',
                  slug: 'ai-orchestrator',
                },
                {
                  title: 'Slack Mentorship Ecosystem',
                  status: 'Beta',
                  statusColor: 'bg-accent-50 text-accent-700 border-accent-200',
                  summary: 'Daily standups, peer code help, and AI-assisted mentoring inside Slack.',
                  slug: 'slack-mentorship-ecosystem',
                },
              ].map((project) => (
                <Link
                  key={project.slug}
                  to={`/projects/${project.slug}`}
                  className="block bg-white rounded-xl border border-secondary-200 p-6 hover:shadow-md transition-shadow no-underline hover:no-underline"
                >
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${project.statusColor} mb-3`}
                  >
                    {project.status}
                  </span>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-sm text-secondary-600 leading-relaxed">
                    {project.summary}
                  </p>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Link
                to="/projects"
                className="btn btn-primary inline-flex items-center no-underline hover:no-underline"
              >
                View All Projects
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          aria-labelledby="cta-heading"
          className="relative py-20 bg-gradient-hero overflow-hidden"
        >
          {/* Watermark banner */}
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <img
              src="/brand/2ndStoryBanner.png"
              alt=""
              className="w-[700px] sm:w-[900px] max-w-none opacity-[0.03] select-none"
              draggable="false"
            />
          </div>
          <div className="relative max-w-3xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-semibold text-primary-300 uppercase tracking-widest mb-3">
              Get Involved
            </p>
            <h2 id="cta-heading" className="text-3xl font-bold text-white mb-4">
              Ready to Write Your Second Story?
            </h2>
            <p className="text-lg text-secondary-300 mb-10 leading-relaxed max-w-none">
              Whether you are a prospective learner, potential mentor, employer partner,
              or supporter — we would love to connect.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="btn btn-lg bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center shadow-medium no-underline hover:no-underline"
              >
                Create Your Account
                <Target className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
              <Link
                to="/donate"
                className="btn btn-lg border-2 border-white/70 text-white hover:bg-white/10 inline-flex items-center no-underline hover:no-underline"
              >
                <Heart className="mr-2 h-5 w-5" aria-hidden="true" />
                Support Our Mission
              </Link>
              <a
                href="mailto:eHunter@straydog-secondstory.org"
                className="btn btn-lg border-2 border-white/30 text-secondary-300 hover:text-white hover:bg-white/10 inline-flex items-center no-underline hover:no-underline"
                aria-label="Send email to eHunter@straydog-secondstory.org"
              >
                <Mail className="mr-2 h-5 w-5" aria-hidden="true" />
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default HomePage;